import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutocompleteProps } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { BuyerService } from '../../services/buyer.service';
import { AuthService } from '../../services/auth.service';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import SearchableSelect from '../common/SearchableSelect';

/** How many buyers to preload when the box is opened with an empty search. */
export const BUYER_DEFAULT_PAGE_SIZE = 10;
/** How many matches to fetch once the user has typed something. */
export const BUYER_SEARCH_PAGE_SIZE = 20;

const SEARCH_DEBOUNCE_MS = 300;

export const buyerLabel = (option: IBuyer): string => `${option.name} (${option.code})`;

interface UseBuyerSearchOptions {
    /** Set false to stop fetching (e.g. the picker is disabled). Defaults to true. */
    enabled?: boolean;
    /** Only offer active buyers — off by default so an existing reference (an already-inactive
     *  buyer on a style/order) is never dropped out from under a picker showing it. */
    activeOnly?: boolean;
    onError?: (error: unknown) => void;
}

/**
 * Backs a buyer picker: preloads the first page of buyers so an empty, freshly-focused box
 * already lists options, then debounce-searches the server as the user types. Mirrors
 * `EmployeeAutocomplete.tsx`'s `useEmployeeSearch` — never hand-roll a picker in a screen.
 */
export const useBuyerSearch = ({ enabled = true, activeOnly = false, onError }: UseBuyerSearchOptions = {}) => {
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<IBuyer[]>([]);
    const [loading, setLoading] = useState(false);

    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    useEffect(() => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!enabled || !institutionId) {
            setOptions([]);
            return;
        }
        const search = inputValue.trim();
        let cancelled = false;
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                const size = search ? BUYER_SEARCH_PAGE_SIZE : BUYER_DEFAULT_PAGE_SIZE;
                const res = await BuyerService.list(institutionId, 0, size, search || undefined, activeOnly);
                if (!cancelled) setOptions(res.content || []);
            } catch (err) {
                if (!cancelled) {
                    setOptions([]);
                    onErrorRef.current?.(err);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, search ? SEARCH_DEBOUNCE_MS : 0);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [inputValue, enabled, activeOnly]);

    const handleInputChange = useCallback(
        (_event: React.SyntheticEvent, value: string, reason: string) => {
            if (reason === 'input') setInputValue(value);
            else if (reason === 'clear') setInputValue('');
        },
        []
    );

    return { options, loading, inputValue, setInputValue, handleInputChange };
};

/** Keeps the selected value present in the option list so MUI can match it. */
const withSelected = (options: IBuyer[], selected: IBuyer | null): IBuyer[] => {
    if (!selected) return options;
    return options.some(o => o.id === selected.id) ? options : [selected, ...options];
};

type PassthroughProps = Partial<Pick<AutocompleteProps<IBuyer, false, false, false>, 'openOnFocus' | 'autoHighlight'>>;

interface BuyerAutocompleteProps extends PassthroughProps {
    value: IBuyer | null;
    onChange: (value: IBuyer | null) => void;
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    sx?: SxProps<Theme>;
    activeOnly?: boolean;
    onError?: (error: unknown) => void;
    'data-testid'?: string;
}

/** Buyer picker used across the merchandising screens (Style/Sample Request/Buyer Order forms). */
export function BuyerAutocomplete({
    value,
    onChange,
    label = 'Buyer',
    placeholder = 'Search buyers...',
    size = 'small',
    fullWidth = true,
    disabled = false,
    required,
    error,
    helperText,
    sx,
    activeOnly,
    onError,
    ...rest
}: BuyerAutocompleteProps) {
    const { options, loading, inputValue, setInputValue } = useBuyerSearch({
        enabled: !disabled,
        activeOnly,
        onError,
    });

    const mergedOptions = useMemo(() => withSelected(options, value), [options, value]);

    return (
        <SearchableSelect<IBuyer>
            options={mergedOptions}
            value={value ? value.id : ''}
            selectedOption={value}
            onChange={(_id, option) => onChange(option)}
            getOptionLabel={buyerLabel}
            // The server has already filtered; re-filtering here would hide matches.
            disableClientFilter
            onInputChange={setInputValue}
            loading={loading}
            disabled={disabled}
            fullWidth={fullWidth}
            size={size}
            sx={sx}
            label={label}
            placeholder={placeholder}
            required={required}
            error={error}
            helperText={helperText}
            noOptionsText={loading ? 'Loading…' : inputValue ? 'No buyers found' : 'No buyers'}
            {...rest}
        />
    );
}

export default BuyerAutocomplete;
